import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ServerMessage, WireMessage, WireParticipant } from '@kidzim/shared';
import { getAccessToken } from './api';

export type ChatStatus = 'connecting' | 'waiting' | 'open' | 'closed' | 'error';

interface ChatState {
  status: ChatStatus;
  messages: WireMessage[];
  participants: WireParticipant[];
  /** participantId → epoch-ms after which their "typing" bubble should clear. */
  typing: Record<string, number>;
  opensAt: string | null;
  /** Why we're `waiting` or `closed` — e.g. 'scheduled', 'budget_exhausted'. */
  reason: string | null;
  notice: string | null;
  /** Buddy-chat (DM) daily time left, in seconds; null = unlimited or not a DM. */
  remainingSeconds: number | null;
  /** Set once `welcome` arrives — a chat room's name, or the buddy's name for a DM. */
  title: string | null;
  /** Set once `welcome` arrives — 'dm' drives buddy-chat-specific chrome/copy. */
  kind: 'standard' | 'dm' | null;
}

/** How long a "typing" signal stays live if no follow-up arrives (ms). */
const TYPING_TTL = 6000;

function wsUrl(roomId: string): string {
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${window.location.host}/ws/rooms/${encodeURIComponent(roomId)}`;
}

/** Manage a single chat room WebSocket connection with auto-reconnect. */
export function useChat(roomId: string | null, enabled: boolean) {
  const [state, setState] = useState<ChatState>({
    status: 'connecting',
    messages: [],
    participants: [],
    typing: {},
    opensAt: null,
    reason: null,
    notice: null,
    remainingSeconds: null,
    title: null,
    kind: null,
  });
  const socketRef = useRef<WebSocket | null>(null);
  const retryRef = useRef(0);

  const connect = useCallback(() => {
    if (!roomId || !enabled) return;
    const token = getAccessToken();
    if (!token) return;

    const ws = new WebSocket(wsUrl(roomId), ['kidzim.bearer', token]);
    socketRef.current = ws;

    ws.onopen = () => {
      retryRef.current = 0;
      ws.send(JSON.stringify({ t: 'hello' }));
    };

    ws.onmessage = (ev) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(ev.data as string) as ServerMessage;
      } catch {
        return;
      }
      setState((prev) => reduce(prev, msg));
    };

    ws.onclose = (ev) => {
      if (ev.code === 4004) {
        setState((p) => ({ ...p, status: 'closed' }));
        return;
      }
      if (ev.code === 4001 || ev.code === 4003) {
        setState((p) => ({ ...p, status: 'error' }));
        return;
      }
      // Transient close: back off and reconnect (capped).
      if (enabled && retryRef.current < 6) {
        const delay = Math.min(1000 * 2 ** retryRef.current, 10000);
        retryRef.current += 1;
        setTimeout(connect, delay);
      }
    };

    ws.onerror = () => ws.close();
  }, [roomId, enabled]);

  useEffect(() => {
    connect();
    return () => {
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [connect]);

  const send = useCallback((body: string) => {
    const ws = socketRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const nonce = crypto.randomUUID();
    ws.send(JSON.stringify({ t: 'send', nonce, body }));
  }, []);

  const setTyping = useCallback((on: boolean) => {
    const ws = socketRef.current;
    if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ t: 'typing', on }));
  }, []);

  // Re-render on a timer so stale "typing" bubbles fade even if no `off` frame
  // arrives (e.g. the other device dropped). Only ticks while someone is typing.
  const [, force] = useState(0);
  const anyTyping = Object.keys(state.typing).length > 0;
  useEffect(() => {
    if (!anyTyping) return;
    const t = setInterval(() => force((n) => n + 1), 1500);
    return () => clearInterval(t);
  }, [anyTyping]);

  // Screen names of OTHER participants currently typing (own id excluded by the
  // server, which never echoes a typing frame back to its sender).
  const typingNames = useMemo(() => {
    const now = Date.now();
    const byId = new Map(state.participants.map((p) => [p.id, p.screenName]));
    return Object.entries(state.typing)
      .filter(([, expiresAt]) => expiresAt > now)
      .map(([id]) => byId.get(id))
      .filter((name): name is string => Boolean(name));
  }, [state.typing, state.participants]);

  return { ...state, typingNames, send, setTyping };
}

function reduce(prev: ChatState, msg: ServerMessage): ChatState {
  switch (msg.t) {
    case 'welcome':
      return { ...prev, status: 'open', participants: msg.participants, title: msg.title, kind: msg.kind };
    case 'waiting':
      return { ...prev, status: 'waiting', opensAt: msg.opensAt, reason: msg.reason };
    case 'history':
      return { ...prev, messages: dedupe([...msg.messages, ...prev.messages]) };
    case 'message': {
      // A delivered message means the author has stopped typing.
      const typing = { ...prev.typing };
      delete typing[msg.message.authorId];
      return { ...prev, messages: dedupe([...prev.messages, msg.message]), typing };
    }
    case 'presence': {
      const others = prev.participants.filter((p) => p.id !== msg.participant.id);
      const typing = { ...prev.typing };
      if (!msg.participant.online) delete typing[msg.participant.id];
      return { ...prev, participants: msg.participant.online ? [...others, msg.participant] : others, typing };
    }
    case 'typing': {
      const typing = { ...prev.typing };
      if (msg.on) typing[msg.participantId] = Date.now() + TYPING_TTL;
      else delete typing[msg.participantId];
      return { ...prev, typing };
    }
    case 'notice':
      return { ...prev, notice: msg.text };
    case 'closed':
      return { ...prev, status: 'closed', reason: msg.reason };
    case 'im_usage':
      return { ...prev, remainingSeconds: msg.remainingSeconds };
    case 'error':
      return { ...prev, notice: msg.text };
    default:
      return prev;
  }
}

function dedupe(messages: WireMessage[]): WireMessage[] {
  const seen = new Set<string>();
  const out: WireMessage[] = [];
  for (const m of messages) {
    if (seen.has(m.id)) continue;
    seen.add(m.id);
    out.push(m);
  }
  return out.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}
